import { useEffect, useState } from "react";
import { Table, Tag, Button, Select, Space, notification, Flex, DatePicker, Modal, Input, Typography, Tabs, Form, InputNumber } from "antd";
import axiosInstance from "../axios";
import { useAtom } from "jotai";
import { userRole } from "../states/userRole";
import { USER_ROLES } from "../constants/roleConstants";
import dayjs from "dayjs";
import { EyeOutlined, CheckOutlined, CloseOutlined, BankOutlined } from "@ant-design/icons";

interface TutorInfo {
  id: number;
  fullname?: string;
  phone?: string;
  razorpay_linked_account_id: string | null;
  razorpay_account_status: string | null;
  bank_details: Record<string, string | null>;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    phone?: string;
    email?: string;
  };
}

interface TransferRequestRecord {
  id: number;
  tutor_id: number;
  payment_id: number;
  deal_id: number;
  razorpay_linked_account_id: string | null;
  razorpay_fund_account_id: string | null;
  amount_type: "percentage" | "fixed";
  amount_value: number;
  calculated_amount: number;
  razorpay_transfer_id: string | null;
  transfer_status: "PENDING" | "APPROVED" | "APPROVED_IN_PROGRESS" | "REJECTED" | "TRANSFERRED" | "FAILED" | "MANUAL_PENDING";
  transfer_type: "RAZORPAY" | "MANUAL" | null;
  requested_by: number;
  approved_by: number | null;
  rejected_reason: string | null;
  notes: string | null;
  manual_transfer_mode: "BANK_TRANSFER" | "UPI" | "CASH" | null;
  manual_transaction_ref: string | null;
  manual_transfer_date: string | null;
  manual_notes: string | null;
  created_at: string;
  updated_at: string;
  tutor: TutorInfo | null;
  requester: { first_name: string; last_name: string } | null;
  approver: { first_name: string; last_name: string } | null;
}

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Title } = Typography;

const STATUS_COLORS: Record<string, string> = {
  PENDING: "orange",
  APPROVED: "blue",
  APPROVED_IN_PROGRESS: "geekblue",
  REJECTED: "red",
  TRANSFERRED: "green",
  FAILED: "red",
  MANUAL_PENDING: "purple",
};

const MANUAL_MODE_OPTIONS = [
  { value: "BANK_TRANSFER", label: "Bank Transfer (NEFT/RTGS/IMPS)" },
  { value: "UPI", label: "UPI" },
  { value: "CASH", label: "Cash" },
];

const TutorTransferApproval = () => {
  const [role] = useAtom(userRole);
  const isSuperadmin = role === USER_ROLES.SUPERADMIN;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TransferRequestRecord[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    status: "",
    from_date: "",
    to_date: "",
    tutor_id: "",
  });
  const [detailModal, setDetailModal] = useState<{ open: boolean; record: TransferRequestRecord | null }>({
    open: false,
    record: null,
  });
  const [rejectModal, setRejectModal] = useState<{
    open: boolean;
    record: TransferRequestRecord | null;
    reason: string;
  }>({ open: false, record: null, reason: "" });
  const [modeSelectModal, setModeSelectModal] = useState<{ open: boolean; record: TransferRequestRecord | null }>({
    open: false,
    record: null,
  });
  const [manualModal, setManualModal] = useState<{
    open: boolean;
    record: TransferRequestRecord | null;
    mode: string;
    transactionRef: string;
    transferDate: string;
    notes: string;
  }>({
    open: false,
    record: null,
    mode: "",
    transactionRef: "",
    transferDate: dayjs().format("YYYY-MM-DD"),
    notes: "",
  });
  const [actionLoading, setActionLoading] = useState(false);

  // ─── Create Transfer Request ──────────────────────────────────────────────
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createTab, setCreateTab] = useState<"razorpay" | "manual">("razorpay");
  const [createForm, setCreateForm] = useState({
    tutor_id: "",
    payment_id: "",
    deal_id: "",
    amount_type: "percentage" as "percentage" | "fixed",
    amount_value: "",
    calculated_amount: "",
    notes: "",
  });
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, filters.status, filters.from_date, filters.to_date]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        status: filters.status || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
        tutor_id: filters.tutor_id || undefined,
      };
      const response = await axiosInstance.get(`/v1/cre/tutor/transfer-request`, { params });
      if (response.data.is_success) {
        setData(response.data.data.data || []);
        setPagination((prev) => ({ ...prev, total: response.data.data.total || 0 }));
      }
    } catch {
      notification.error({ message: "Failed to load transfer requests" });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number, transferType: "RAZORPAY" | "MANUAL" = "RAZORPAY") => {
    setActionLoading(true);
    try {
      const response = await axiosInstance.post(`/v1/cre/tutor/transfer-request/${id}/approve`, {
        transfer_type: transferType,
      });
      if (response.data.is_success) {
        if (transferType === "MANUAL") {
          notification.success({ message: "Manual transfer initiated — complete it once done" });
        } else {
          notification.success({ message: "Transfer approved and processing started" });
        }
        setModeSelectModal({ open: false, record: null });
        setDetailModal({ open: false, record: null });
        fetchData();
      } else {
        notification.error({ message: response.data.error?.message || "Approval failed" });
      }
    } catch (error: any) {
      notification.error({ message: error?.response?.data?.error?.message || "Approval failed" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteManual = async () => {
    if (!manualModal.record || !manualModal.mode || !manualModal.transactionRef) {
      notification.warning({ message: "Transfer mode and transaction reference are required" });
      return;
    }
    setActionLoading(true);
    try {
      const response = await axiosInstance.post(
        `/v1/cre/tutor/transfer-request/${manualModal.record.id}/complete-manual`,
        {
          manual_transfer_mode: manualModal.mode,
          manual_transaction_ref: manualModal.transactionRef,
          manual_transfer_date: manualModal.transferDate || dayjs().format("YYYY-MM-DD"),
          manual_notes: manualModal.notes || null,
        },
      );
      if (response.data.is_success) {
        notification.success({ message: "Manual transfer completed successfully" });
        setManualModal({ open: false, record: null, mode: "", transactionRef: "", transferDate: "", notes: "" });
        setDetailModal({ open: false, record: null });
        fetchData();
      } else {
        notification.error({ message: response.data.error?.message || "Complete failed" });
      }
    } catch (error: any) {
      notification.error({ message: error?.response?.data?.error?.message || "Complete failed" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.record) return;
    setActionLoading(true);
    try {
      const response = await axiosInstance.post(`/v1/cre/tutor/transfer-request/${rejectModal.record.id}/reject`, {
        reason: rejectModal.reason,
      });
      if (response.data.is_success) {
        notification.success({ message: "Transfer rejected" });
        setRejectModal({ open: false, record: null, reason: "" });
        fetchData();
      } else {
        notification.error({ message: response.data.error?.message || "Rejection failed" });
      }
    } catch (error: any) {
      notification.error({ message: error?.response?.data?.error?.message || "Rejection failed" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedRowKeys.length === 0) {
      notification.warning({ message: "Please select at least one request to approve" });
      return;
    }
    setActionLoading(true);
    try {
      const response = await axiosInstance.post(`/v1/cre/tutor/transfer-request/bulk-approve`, {
        ids: selectedRowKeys,
      });
      if (response.data.is_success) {
        const { success, failed } = response.data.data;
        if (failed.length === 0) {
          notification.success({ message: `All ${success.length} transfers approved successfully!` });
        } else {
          notification.warning({ message: `${success.length} approved, ${failed.length} failed` });
        }
        setSelectedRowKeys([]);
        fetchData();
      } else {
        notification.error({ message: response.data.error?.message || "Bulk approval failed" });
      }
    } catch (error: any) {
      notification.error({ message: error?.response?.data?.error?.message || "Bulk approval failed" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateTransfer = async () => {
    if (!createForm.tutor_id || !createForm.payment_id || !createForm.deal_id || !createForm.calculated_amount) {
      notification.warning({ message: "Please fill all required fields" });
      return;
    }
    setCreateLoading(true);
    try {
      const transfer_type = createTab === "manual" ? "MANUAL" : "RAZORPAY";
      const response = await axiosInstance.post(`/v1/cre/tutor/transfer-request`, {
        tutor_id: Number(createForm.tutor_id),
        payment_id: Number(createForm.payment_id),
        deal_id: Number(createForm.deal_id),
        amount_type: createForm.amount_type,
        amount_value: Number(createForm.amount_value),
        calculated_amount: Number(createForm.calculated_amount),
        notes: createForm.notes || null,
        transfer_type,
      });
      if (response.data.is_success) {
        notification.success({ message: "Transfer request created successfully" });
        setCreateModalOpen(false);
        setCreateForm({ tutor_id: "", payment_id: "", deal_id: "", amount_type: "percentage", amount_value: "", calculated_amount: "", notes: "" });
        fetchData();
      } else {
        notification.error({ message: response.data.error?.message || "Failed to create transfer request" });
      }
    } catch (error: any) {
      notification.error({ message: error?.response?.data?.error?.message || "Failed to create transfer request" });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDateChange = (_dates: any, dateStrings: string[]) => {
    setFilters((prev) => ({
      ...prev,
      from_date: dateStrings[0] || "",
      to_date: dateStrings[1] || "",
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "created_at",
      width: 100,
      render: (date: string) => (date ? dayjs(date).format("DD MMM YY") : "-"),
    },
    {
      title: "Deal ID",
      dataIndex: "deal_id",
      width: 80,
      render: (id: number) => (
        <a href={`/student/${id}`} target="_blank" rel="noreferrer">
          {id}
        </a>
      ),
    },
    {
      title: "Tutor",
      width: 180,
      render: (_: any, record: any) => {
        const tutor = record.tutor || {};
        const tutorUser = tutor.user || {};
        const tutorName = [tutorUser.first_name, tutorUser.last_name].filter(Boolean).join(" ") || tutor.fullname || "-";
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{tutorName}</div>
            <div style={{ fontSize: 11, color: "#888" }}>ID: {tutor.id}</div>
          </div>
        );
      },
    },
    {
      title: "Razorpay Account",
      width: 160,
      render: (_: any, record: any) => {
        const tutor = record.tutor || {};
        return (
          <div>
            <Tag color={tutor.razorpay_account_status === "active" ? "green" : "orange"} style={{ fontSize: 11 }}>
              {tutor.razorpay_account_status || "N/A"}
            </Tag>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{tutor.razorpay_linked_account_id || "-"}</div>
          </div>
        );
      },
    },
    {
      title: "Amount Type",
      dataIndex: "amount_type",
      width: 100,
      render: (type: string) => (
        <Tag color={type === "percentage" ? "blue" : "purple"} style={{ textTransform: "capitalize" }}>
          {type}
        </Tag>
      ),
    },
    {
      title: "Value",
      dataIndex: "amount_value",
      width: 80,
      render: (val: number, record: any) => (record.amount_type === "percentage" ? `${val}%` : `₹${val}`),
    },
    {
      title: "Transfer Amount",
      dataIndex: "calculated_amount",
      width: 130,
      render: (amt: number) => <strong style={{ color: "#52c41a" }}>₹{amt}</strong>,
    },
    {
      title: "Type",
      dataIndex: "transfer_type",
      width: 90,
      render: (type: string | null) => (
        <Tag color={type === "MANUAL" ? "purple" : type === "RAZORPAY" ? "cyan" : "default"}>
          {type || "-"}
        </Tag>
      ),
    },
    {
      title: "Razorpay Transfer",
      dataIndex: "razorpay_transfer_id",
      width: 150,
      render: (id: string) => (id ? <span style={{ fontSize: 11 }}>{id}</span> : "-"),
    },
    {
      title: "Status",
      dataIndex: "transfer_status",
      width: 130,
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Requested By",
      width: 120,
      render: (_: any, record: any) => {
        const req = record.requester || {};
        return `${req.first_name || ""} ${req.last_name || ""}`.trim() || "-";
      },
    },
    {
      title: "Actions",
      width: 180,
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EyeOutlined />} size="small" onClick={() => setDetailModal({ open: true, record })} />
          {record.transfer_status === "PENDING" && isSuperadmin && (
            <>
              <Button
                icon={<CheckOutlined />}
                size="small"
                type="primary"
                onClick={() => setModeSelectModal({ open: true, record })}
                loading={actionLoading}
              />
              <Button
                icon={<CloseOutlined />}
                size="small"
                danger
                onClick={() => setRejectModal({ open: true, record, reason: "" })}
              />
            </>
          )}
          {record.transfer_status === "MANUAL_PENDING" && isSuperadmin && (
            <>
              <Button
                icon={<CheckOutlined />}
                size="small"
                type="primary"
                onClick={() =>
                  setManualModal({
                    open: true,
                    record,
                    mode: record.manual_transfer_mode || "",
                    transactionRef: record.manual_transaction_ref || "",
                    transferDate: record.manual_transfer_date || dayjs().format("YYYY-MM-DD"),
                    notes: record.manual_notes || "",
                  })
                }
                loading={actionLoading}
              />
              <Button
                icon={<CloseOutlined />}
                size="small"
                danger
                onClick={() => setRejectModal({ open: true, record, reason: "" })}
              />
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0 }}>
          Tutor Transfer Approval
        </Title>
        <Flex gap={8}>
          {selectedRowKeys.length > 0 && isSuperadmin && (
            <Button type="primary" onClick={handleBulkApprove} loading={actionLoading}>
              Approve Selected ({selectedRowKeys.length})
            </Button>
          )}
        </Flex>
      </Flex>

      {/* Filters */}
      <Flex gap={12} style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="Status"
          allowClear
          style={{ width: 160 }}
          value={filters.status || undefined}
          onChange={(val) => {
            setFilters((prev) => ({ ...prev, status: val || "" }));
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
        >
          {["PENDING", "APPROVED", "APPROVED_IN_PROGRESS", "MANUAL_PENDING", "REJECTED", "TRANSFERRED", "FAILED"].map((s) => (
            <Select.Option key={s} value={s}>
              {s}
            </Select.Option>
          ))}
        </Select>

        <RangePicker onChange={handleDateChange} />

        <Input
          placeholder="Tutor ID"
          style={{ width: 120 }}
          allowClear
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, tutor_id: e.target.value || "" }));
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
        />

        <Button onClick={fetchData} loading={loading}>
          Refresh
        </Button>
      </Flex>

      {/* Table */}
      <Table
        rowKey="id"
        dataSource={data}
        loading={loading}
        rowSelection={
          isSuperadmin
            ? {
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                getCheckboxProps: (record: any) => ({ disabled: record.transfer_status !== "PENDING" }),
              }
            : undefined
        }
        columns={columns}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
        scroll={{ x: 1500 }}
        size="small"
      />

      {/* Detail Modal */}
      <Modal
        title="Transfer Request Details"
        open={detailModal.open}
        onCancel={() => setDetailModal({ open: false, record: null })}
        footer={null}
        width={600}
      >
        {detailModal.record ? (
          (() => {
            const r = detailModal.record;
            return (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div>
                    <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Request ID</p>
                    <p style={{ margin: 0, fontWeight: 500 }}>{r.id}</p>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Status</p>
                    <Tag color={STATUS_COLORS[r.transfer_status] || "default"}>{r.transfer_status}</Tag>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Deal ID</p>
                    <p style={{ margin: 0, fontWeight: 500 }}>{r.deal_id}</p>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Payment ID</p>
                    <p style={{ margin: 0, fontWeight: 500 }}>{r.payment_id}</p>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Transfer Type</p>
                    <Tag color={r.transfer_type === "MANUAL" ? "purple" : r.transfer_type === "RAZORPAY" ? "cyan" : "default"}>
                      {r.transfer_type || "-"}
                    </Tag>
                  </div>
                </div>

                {/* Tutor Info */}
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ marginBottom: 8 }}>Tutor</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>
                      <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Name</p>
                      <p style={{ margin: 0, fontWeight: 500 }}>
                        {[r.tutor?.user?.first_name, r.tutor?.user?.last_name].filter(Boolean).join(" ") || r.tutor?.fullname || "-"}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Phone</p>
                      <p style={{ margin: 0, fontWeight: 500 }}>{r.tutor?.user?.phone || r.tutor?.phone || "-"}</p>
                    </div>
                    <div>
                      <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Razorpay Account</p>
                      <Tag color={r.tutor?.razorpay_account_status === "active" ? "green" : "orange"}>
                        {r.tutor?.razorpay_linked_account_id || "Not Created"}
                      </Tag>
                    </div>
                    <div>
                      <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Account Status</p>
                      <p style={{ margin: 0, fontWeight: 500 }}>{r.tutor?.razorpay_account_status || "-"}</p>
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                {r.tutor?.bank_details && Object.keys(r.tutor.bank_details).length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ marginBottom: 8 }}>Bank Details</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {[
                        { label: "Bank", value: r.tutor.bank_details.bankName },
                        { label: "Account Number", value: r.tutor.bank_details.accountNumber },
                        { label: "IFSC", value: r.tutor.bank_details.ifscCode },
                        { label: "Holder Name", value: r.tutor.bank_details.accountHolderName },
                      ].map((item) => (
                        <div key={item.label}>
                          <p style={{ margin: 0, color: "#888", fontSize: 12 }}>{item.label}</p>
                          <p style={{ margin: 0, fontWeight: 500 }}>{item.value || "-"}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Amount */}
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ marginBottom: 8 }}>Transfer Amount</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>
                      <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Type</p>
                      <Tag color={r.amount_type === "percentage" ? "blue" : "purple"}>{r.amount_type}</Tag>
                    </div>
                    <div>
                      <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Value</p>
                      <p style={{ margin: 0, fontWeight: 500 }}>
                        {r.amount_type === "percentage" ? `${r.amount_value}%` : `₹${r.amount_value}`}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Transfer Amount</p>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "#52c41a" }}>
                        ₹{r.calculated_amount}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Razorpay Transfer ID</p>
                      <p style={{ margin: 0, fontWeight: 500, fontSize: 11 }}>{r.razorpay_transfer_id || "-"}</p>
                    </div>
                  </div>
                </div>

                {/* Manual Transfer Details */}
                {r.transfer_type === "MANUAL" && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ marginBottom: 8 }}>Manual Transfer Details</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <div>
                        <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Mode</p>
                        <p style={{ margin: 0, fontWeight: 500 }}>
                          {r.manual_transfer_mode === "BANK_TRANSFER" ? "Bank Transfer (NEFT/RTGS/IMPS)" : r.manual_transfer_mode || "-"}
                        </p>
                      </div>
                      <div>
                        <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Transaction Ref</p>
                        <p style={{ margin: 0, fontWeight: 500 }}>{r.manual_transaction_ref || "-"}</p>
                      </div>
                      <div>
                        <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Transfer Date</p>
                        <p style={{ margin: 0, fontWeight: 500 }}>
                          {r.manual_transfer_date ? dayjs(r.manual_transfer_date).format("DD MMM YYYY") : "-"}
                        </p>
                      </div>
                      {r.manual_notes && (
                        <div style={{ gridColumn: "1 / -1" }}>
                          <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Notes</p>
                          <p style={{ margin: 0, fontWeight: 500 }}>{r.manual_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {r.notes && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ marginBottom: 8 }}>Notes</h4>
                    <p style={{ margin: 0, fontSize: 13 }}>{r.notes}</p>
                  </div>
                )}

                {/* Rejected Reason */}
                {r.rejected_reason && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ marginBottom: 8 }}>Rejection Reason</h4>
                    <p style={{ margin: 0, fontSize: 13, color: "#ff4d4f" }}>{r.rejected_reason}</p>
                  </div>
                )}

                {/* Actions */}
                {r.transfer_status === "PENDING" && isSuperadmin && (
                  <Flex gap={12}>
                    <Button
                      type="primary"
                      onClick={() => setModeSelectModal({ open: true, record: r })}
                      loading={actionLoading}
                      icon={<CheckOutlined />}
                    >
                      Approve
                    </Button>
                    <Button
                      danger
                      onClick={() => {
                        setDetailModal({ open: false, record: null });
                        setRejectModal({ open: true, record: r, reason: "" });
                      }}
                      icon={<CloseOutlined />}
                    >
                      Reject
                    </Button>
                  </Flex>
                )}
                {r.transfer_status === "MANUAL_PENDING" && isSuperadmin && (
                  <Flex gap={12}>
                    <Button
                      type="primary"
                      onClick={() =>
                        setManualModal({
                          open: true,
                          record: r,
                          mode: r.manual_transfer_mode || "",
                          transactionRef: r.manual_transaction_ref || "",
                          transferDate: r.manual_transfer_date || dayjs().format("YYYY-MM-DD"),
                          notes: r.manual_notes || "",
                        })
                      }
                      loading={actionLoading}
                      icon={<CheckOutlined />}
                    >
                      Complete Manual Transfer
                    </Button>
                    <Button
                      danger
                      onClick={() => {
                        setDetailModal({ open: false, record: null });
                        setRejectModal({ open: true, record: r, reason: "" });
                      }}
                      icon={<CloseOutlined />}
                    >
                      Reject
                    </Button>
                  </Flex>
                )}
              </div>
            );
          })()
        ) : (
          <div style={{ textAlign: "center", padding: 20 }}>
            <p style={{ color: "#888" }}>No record selected</p>
          </div>
        )}
      </Modal>

      {/* Mode Select Modal */}
      <Modal
        title="Select Transfer Mode"
        open={modeSelectModal.open}
        onCancel={() => setModeSelectModal({ open: false, record: null })}
        footer={null}
        width={400}
      >
        {modeSelectModal.record && (
          <div>
            <p style={{ marginBottom: 16, color: "#666" }}>
              Choose how to transfer <strong>₹{modeSelectModal.record.calculated_amount}</strong> to tutor{" "}
              <strong>{modeSelectModal.record.tutor?.fullname || modeSelectModal.record.tutor?.user?.first_name || "N/A"}</strong>.
            </p>
            <Flex vertical gap={12}>
              <Button
                block
                size="large"
                type="primary"
                icon={<BankOutlined />}
                onClick={() => handleApprove(modeSelectModal.record!.id, "RAZORPAY")}
                loading={actionLoading}
              >
                Razorpay (Automatic)
              </Button>
              <Button
                block
                size="large"
                icon={<CheckOutlined />}
                onClick={() => {
                  setModeSelectModal({ open: false, record: null });
                  setManualModal({
                    open: true,
                    record: modeSelectModal.record,
                    mode: "",
                    transactionRef: "",
                    transferDate: dayjs().format("YYYY-MM-DD"),
                    notes: "",
                  });
                }}
              >
                Manual (Bank / UPI / Cash)
              </Button>
            </Flex>
          </div>
        )}
      </Modal>

      {/* Manual Complete Modal */}
      <Modal
        title="Complete Manual Transfer"
        open={manualModal.open}
        onCancel={() => setManualModal({ open: false, record: null, mode: "", transactionRef: "", transferDate: dayjs().format("YYYY-MM-DD"), notes: "" })}
        onOk={handleCompleteManual}
        okText="Mark as Completed"
        okButtonProps={{ loading: actionLoading, disabled: !manualModal.mode || !manualModal.transactionRef }}
        width={480}
      >
        {manualModal.record && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 8 }}>
              <p style={{ margin: 0, fontSize: 12, color: "#888" }}>Transfer Amount</p>
              <p style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#52c41a" }}>₹{manualModal.record.calculated_amount}</p>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Transfer Mode *</label>
              <Select
                placeholder="Select transfer mode"
                style={{ width: "100%" }}
                value={manualModal.mode || undefined}
                onChange={(val) => setManualModal((prev) => ({ ...prev, mode: val }))}
              >
                {MANUAL_MODE_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Transaction Reference *</label>
              <Input
                placeholder="UTRN / Transaction ID / Cheque No. / Reference"
                value={manualModal.transactionRef}
                onChange={(e) => setManualModal((prev) => ({ ...prev, transactionRef: e.target.value }))}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Transfer Date</label>
              <DatePicker
                style={{ width: "100%" }}
                value={manualModal.transferDate ? dayjs(manualModal.transferDate) : undefined}
                onChange={(date) => setManualModal((prev) => ({ ...prev, transferDate: date?.format("YYYY-MM-DD") || "" }))}
                format="DD MMM YYYY"
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Notes</label>
              <TextArea
                rows={2}
                placeholder="Additional notes (optional)"
                value={manualModal.notes}
                onChange={(e) => setManualModal((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Transfer Request"
        open={rejectModal.open}
        onCancel={() => setRejectModal({ open: false, record: null, reason: "" })}
        onOk={handleReject}
        okText="Reject"
        okButtonProps={{ danger: true, loading: actionLoading }}
      >
        <p>Are you sure you want to reject this transfer request?</p>
        <TextArea
          rows={3}
          placeholder="Reason for rejection (optional)"
          value={rejectModal.reason}
          onChange={(e) => setRejectModal((prev) => ({ ...prev, reason: e.target.value }))}
        />
      </Modal>

      {/* Create Transfer Modal */}
      <Modal
        title="Create Transfer Request"
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); setCreateForm({ tutor_id: "", payment_id: "", deal_id: "", amount_type: "percentage", amount_value: "", calculated_amount: "", notes: "" }); }}
        footer={null}
        width={560}
      >
        <Tabs
          activeKey={createTab}
          onChange={(key) => { setCreateTab(key as "razorpay" | "manual"); setCreateForm({ tutor_id: "", payment_id: "", deal_id: "", amount_type: "percentage", amount_value: "", calculated_amount: "", notes: "" }); }}
          items={[
            {
              key: "razorpay",
              label: "Razorpay",
              children: (
                <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "12px 0" }}>
                  <p style={{ margin: 0, fontSize: 12, color: "#52c41a" }}>
                    Requires tutor to have an active Razorpay linked account.
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Tutor ID *</label>
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Tutor ID"
                        value={createForm.tutor_id ? Number(createForm.tutor_id) : undefined}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, tutor_id: val ? String(val) : "" }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Payment ID *</label>
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Payment ID"
                        value={createForm.payment_id ? Number(createForm.payment_id) : undefined}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, payment_id: val ? String(val) : "" }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Deal ID *</label>
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Deal ID"
                        value={createForm.deal_id ? Number(createForm.deal_id) : undefined}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, deal_id: val ? String(val) : "" }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Amount Type</label>
                      <Select
                        style={{ width: "100%" }}
                        value={createForm.amount_type}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, amount_type: val }))}
                      >
                        <Select.Option value="percentage">Percentage</Select.Option>
                        <Select.Option value="fixed">Fixed</Select.Option>
                      </Select>
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Amount Value</label>
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder={createForm.amount_type === "percentage" ? "e.g. 15" : "e.g. 500"}
                        value={createForm.amount_value ? Number(createForm.amount_value) : undefined}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, amount_value: val ? String(val) : "" }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Calculated Amount (₹) *</label>
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="e.g. 1500"
                        value={createForm.calculated_amount ? Number(createForm.calculated_amount) : undefined}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, calculated_amount: val ? String(val) : "" }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Notes</label>
                    <TextArea
                      rows={2}
                      placeholder="Optional notes"
                      value={createForm.notes}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, notes: e.target.value }))}
                    />
                  </div>
                  <Flex justify="end" gap={8}>
                    <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
                    <Button type="primary" loading={createLoading} onClick={handleCreateTransfer}>
                      Create Transfer Request
                    </Button>
                  </Flex>
                </div>
              ),
            },
            {
              key: "manual",
              label: "Manual",
              children: (
                <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "12px 0" }}>
                  <p style={{ margin: 0, fontSize: 12, color: "#722ed1" }}>
                    No Razorpay account required. Record transfers made via Bank, UPI, or Cash.
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Tutor ID *</label>
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Tutor ID"
                        value={createForm.tutor_id ? Number(createForm.tutor_id) : undefined}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, tutor_id: val ? String(val) : "" }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Payment ID *</label>
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Payment ID"
                        value={createForm.payment_id ? Number(createForm.payment_id) : undefined}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, payment_id: val ? String(val) : "" }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Deal ID *</label>
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Deal ID"
                        value={createForm.deal_id ? Number(createForm.deal_id) : undefined}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, deal_id: val ? String(val) : "" }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Amount Type</label>
                      <Select
                        style={{ width: "100%" }}
                        value={createForm.amount_type}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, amount_type: val }))}
                      >
                        <Select.Option value="percentage">Percentage</Select.Option>
                        <Select.Option value="fixed">Fixed</Select.Option>
                      </Select>
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Amount Value</label>
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder={createForm.amount_type === "percentage" ? "e.g. 15" : "e.g. 500"}
                        value={createForm.amount_value ? Number(createForm.amount_value) : undefined}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, amount_value: val ? String(val) : "" }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Calculated Amount (₹) *</label>
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="e.g. 1500"
                        value={createForm.calculated_amount ? Number(createForm.calculated_amount) : undefined}
                        onChange={(val) => setCreateForm((prev) => ({ ...prev, calculated_amount: val ? String(val) : "" }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Notes</label>
                    <TextArea
                      rows={2}
                      placeholder="Optional notes"
                      value={createForm.notes}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, notes: e.target.value }))}
                    />
                  </div>
                  <Flex justify="end" gap={8}>
                    <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
                    <Button type="primary" loading={createLoading} onClick={handleCreateTransfer}>
                      Create Transfer Request
                    </Button>
                  </Flex>
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default TutorTransferApproval;
